import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getBaseParsedFormData,
  getBotInitialAmount,
  getIsReverse,
  validateBotAmount,
  validateCustomFirstEntry,
} from "@shared/features/bots/BotForm/common/utils";
import { toFixed } from "@shared/utils/numbers";
import { isNumber, pickBy } from "lodash";
import {
  AmountType,
  BaseBotData,
  BaseBotGeneralData,
  BotConfigurationType,
  BotData,
  BotGeneralData,
  BotOptions,
  BotType,
  CreateBotRequest,
  OverrideFieldType,
  PrecisionType,
  SymbolsMetadataManager,
} from "@shared/types";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import {
  BotFormType,
  Error,
  FormDataType,
  InitializedData,
  Steps,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import useBotAmount from "@shared/features/bots/BotForm/useBotAmount";
import {
  convertAmountToQuantity,
  getConversionMinCost,
} from "@shared/features/trades/common/utils";
import useBotFormValues from "@shared/features/bots/BotForm/common/hooks/useBotFormValues";
import useBotFormQueries from "@shared/features/bots/BotForm/common/hooks/useBotFormQueries";
import { isSymbol } from "@shared/types/utils";
import { useBotOrders } from "@shared/features/bots/BotForm/common/hooks";
import { useClientsData, useSymbolsMetadata } from "@shared/utils/hooks/misc";
import { sharedLib } from "@shared/services/shared.service";
import { useSelector } from "react-redux";

const DEFAULT_LOADING_END_DELAY_MS = 1000;

interface useBotFormProviderProps {
  botType: BotType;
  botID?: number;
  backtestingBotId?: number;
  botIDs?: number[];
  botUUID?: string;
  botFormType?: BotFormType;
  isChannelConfiguration?: boolean;
  isMobileApp?: boolean;
  isBacktesting?: boolean;
  isDemoCopyPublic?: boolean;
  isCopyToWorkspace?: boolean;
  configurationType?: BotConfigurationType;
  subGroupId?: number;
  loadingEndDelayMs?: number;
}

const useBotFormProvider = ({
  botType,
  botID,
  backtestingBotId,
  botIDs,
  botUUID,
  botFormType,
  isChannelConfiguration,
  isMobileApp,
  isBacktesting,
  isDemoCopyPublic,
  isCopyToWorkspace,
  configurationType,
  subGroupId,
  loadingEndDelayMs = DEFAULT_LOADING_END_DELAY_MS,
}: useBotFormProviderProps) => {
  const { getPrecision } = usePrecisions();
  const isPublic = botFormType === BotFormType.COPY_PUBLIC;
  const isDuplicate = botFormType === BotFormType.DUPLICATE;
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const isBulkAction = isBulkEdit || isBulkCreate;
  const isEdit = isBulkEdit || botFormType === BotFormType.EDIT;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isHedgingBot = botType === BotType.HEDGING;
  const { isSuccess: isClientsSuccess } = useClientsData();
  const currentSymbolsMetadata = useSymbolsMetadata();
  const { getAmountConversionFormula, getQuantityConversionFormula } =
    currentSymbolsMetadata;
  const [symbolsMetadata, setSymbolsMetadata] = useState<
    SymbolsMetadataManager | undefined
  >(undefined);
  const user = useSelector(sharedLib.selectors.authentication.selectRealUser);

  const {
    initializeFormValues,
    formValues,
    formSetters,
    isAdvancedView,
    setIsAdvancedView,
    fieldsToInitialize,
    addFieldToInitialize,
    removeFieldToInitialize,
    amountChangedRef,
  } = useBotFormValues({
    botType,
    botFormType,
    isChannelConfiguration,
    isBacktesting,
    symbolsMetadata,
  });

  const {
    account: formAccount,
    accounts,
    exchanges,
    symbols,
    useSymbolFromAlert,
    entryStrategy,
    amountType,
    amountValue,
    amountPercentageBaseType,
    amountCustomBaseValue,
    longShort,
    multiplier,
    checkedFields,
    overrideFields,
    initialized,
    subGroup,
  } = formValues;
  const account = isSymbol(formAccount) ? undefined : formAccount;
  const parsedMultiplier = isSymbol(multiplier) ? 1 : multiplier || 1;
  const parsedEntryStrategy = isSymbol(entryStrategy)
    ? undefined
    : entryStrategy;
  const { setCheckedFields, setOverrideFields } = formSetters;
  const [errors, setErrors] = useState<Error[]>([]);
  const [localIsLoading, setLocalIsLoading] = useState(true);
  const [initializedData, setInitializedData] = useState<InitializedData>(
    new Set()
  );
  const [tab, setTab] = useState(Steps.GENERAL);
  const [shouldInitialSymbolAndAccount, setShouldInitialSymbolAndAccount] =
    useState(false);

  useEffect(() => {
    if (
      currentSymbolsMetadata.isSuccess &&
      !symbolsMetadata &&
      isClientsSuccess
    ) {
      setSymbolsMetadata(currentSymbolsMetadata);
    }
  }, [currentSymbolsMetadata.isSuccess, isClientsSuccess]);

  const exchangeSymbols =
    symbolsMetadata?.getAccountActiveSymbols(account?.client_id) || [];
  const showBacktesting =
    [BotType.SIGNAL, BotType.ASAP].includes(botType) &&
    !isBulkAction &&
    !user?.is_sub_user;
  const showPublicBots =
    botType === BotType.ASAP &&
    (botFormType === BotFormType.CREATE ||
      botFormType === BotFormType.COPY_PUBLIC);
  const isPublicBotApplied = showPublicBots && isPublic && !!botUUID;

  const {
    getBotDataQuery,
    getPublicBotDataQuery,
    getBotCreateGeneralDataQuery,
    getBotBulkCreateGeneralDataQuery,
    isSymbolValid,
    getBotsNamesQuery,
    getClientsListQuery,
    getExchangeSymbolPriceQuery,
    getExchangeToSymbolPricesQuery,
    getSymbolsMetadataStatus,
    getGroupsDataQuery,
    symbolMetadata,
    useBotCreateGeneralData,
    getBotsDataQuery,
    getBacktestBotDataQuery,
    getDefaultConfigurationDataQuery,
  } = useBotFormQueries({
    isEdit,
    botID,
    backtestingBotId,
    botIDs,
    isDuplicate,
    isPublic,
    botUUID,
    symbols: isSymbol(symbols) ? [] : symbols,
    useSymbolFromAlert,
    botType,
    botFormType,
    account,
    accounts,
    exchanges,
    subGroup,
    isChannelConfiguration,
    isDemoCopyPublic,
    isCopyToWorkspace,
    showBacktesting,
    configurationType,
    subGroupId,
  });
  const { data: botConfigurationData } = getDefaultConfigurationDataQuery;
  const { data: editBotGeneralData } = getBotDataQuery;
  const { currentData: publicBotGeneralData } = getPublicBotDataQuery;
  const { data: createBotGeneralData } = getBotCreateGeneralDataQuery;
  const { data: bulkCreateBotGeneralData } = getBotBulkCreateGeneralDataQuery;
  const backtestBotData = getBacktestBotDataQuery.currentData?.data;
  const { data: botsNames } = getBotsNamesQuery;
  const symbol = isSymbol(symbols) || isSignalsBot ? undefined : symbols[0];
  const reverse = getIsReverse(longShort);

  const { data: botAmountData, status: getBotAmountDataQuery } = useBotAmount({
    botType,
    botFormType,
    amountValue: isSymbol(amountValue) ? undefined : amountValue,
    amountType: isSymbol(amountType) ? undefined : amountType,
    amountPercentageBaseType,
    amountCustomBaseValue: amountCustomBaseValue
      ? parseFloat(amountCustomBaseValue)
      : undefined,
    account,
    symbol,
    skip: isChannelConfiguration || isTradingViewSignalsBot,
    editBotData: isHedgingBot ? editBotGeneralData?.data : undefined,
  });

  const { data: symbolPriceData } = getExchangeSymbolPriceQuery;

  const quantityPrecision = getPrecision(
    PrecisionType.QUANTITY,
    symbol,
    account?.exchange
  );

  const getAmountTypePrecision = (amountType?: AmountType) => {
    let amountPrecision = 2;
    if (amountType === AmountType.FIXED_BTC_AMOUNT)
      amountPrecision = getPrecision(PrecisionType.BTC_AMOUNT);
    else if (amountType === AmountType.FIXED_USD_AMOUNT)
      amountPrecision = getPrecision(PrecisionType.USD_AMOUNT);
    return amountPrecision;
  };
  const amountPrecision = isSymbol(amountType)
    ? 0
    : getAmountTypePrecision(amountType);

  const mainCoinAmountPrecision = botAmountData?.mainCoinAmountPrecision || 0;
  const getMinAmountByPrice = useCallback(
    (price: number): number => {
      return getConversionMinCost(
        true,
        parsedMultiplier,
        mainCoinAmountPrecision,
        quantityPrecision,
        price,
        getAmountConversionFormula(symbol, account?.exchange),
        symbolMetadata?.amount_min,
        symbolMetadata?.quantity_min,
        getQuantityConversionFormula(symbol, account?.exchange)
      );
    },
    [
      parsedMultiplier,
      mainCoinAmountPrecision,
      quantityPrecision,
      symbolMetadata,
    ]
  );

  const validateOrderConvertedAmount = useCallback(
    (amount: number, price: number) => {
      const minQuantity = symbolMetadata?.quantity_min;
      if (!isNumber(minQuantity)) return true;
      const orderQuantity =
        convertAmountToQuantity(
          amount,
          parsedMultiplier,
          price,
          getQuantityConversionFormula(symbol, account?.exchange)
        ) || 0;
      return toFixed(orderQuantity, quantityPrecision) >= minQuantity;
    },
    [parsedMultiplier, botAmountData, quantityPrecision, symbolMetadata]
  );

  useEffect(() => {
    const initializationBotData = isBacktesting
      ? backtestBotData
      : ((editBotGeneralData?.data ||
          publicBotGeneralData ||
          botConfigurationData) as BaseBotData | BotData | undefined);
    if (!initializationBotData || !getSymbolsMetadataStatus.isSuccess) return;
    initializeFormValues(initializationBotData, botsNames);
  }, [
    botConfigurationData,
    editBotGeneralData,
    publicBotGeneralData,
    botsNames,
    backtestBotData,
    backtestingBotId,
    getSymbolsMetadataStatus.isSuccess,
  ]);

  const addError = (error: Error) => {
    setErrors((errors) => {
      return errors.includes(error) ? errors : [...errors, error];
    });
  };

  const addInitializedData = (formDataType: FormDataType) => {
    setInitializedData((initializedData) => {
      if (initializedData.has(formDataType)) return initializedData;
      const updatedInitializedData = new Set(initializedData);
      updatedInitializedData.add(formDataType);
      return updatedInitializedData;
    });
  };

  const removeError = (error: Error) => {
    setErrors((errors) => {
      return errors.includes(error)
        ? errors.filter((iterError) => iterError !== error)
        : errors;
    });
  };

  const toggleField = (field: ToggleField, checked: boolean) => {
    setCheckedFields((prevCheckedFields) => {
      const updatedCheckedFields = new Set(prevCheckedFields);
      if (checked) {
        updatedCheckedFields.add(field);
      } else {
        updatedCheckedFields.delete(field);
      }
      return updatedCheckedFields;
    });
  };

  const toggleOverrideField = (field: OverrideFieldType, checked: boolean) => {
    setOverrideFields((prevOverrideFields) => {
      const updatedOverrideFields = new Set(prevOverrideFields);
      if (checked) {
        updatedOverrideFields.add(field);
      } else {
        updatedOverrideFields.delete(field);
      }
      return updatedOverrideFields;
    });
  };

  const filteredErrors = useMemo(() => {
    return errors.filter((error) => {
      if (error in ERROR_TYPES_TO_TOGGLE_FIELDS) {
        return ERROR_TYPES_TO_TOGGLE_FIELDS[error]?.every((toggleField) =>
          checkedFields.has(toggleField)
        );
      }
      return true;
    });
  }, [errors, checkedFields]);

  const getParsedFormData = (
    ignoreErrors?: Error[]
  ): CreateBotRequest | undefined => {
    const errors = ignoreErrors
      ? filteredErrors.filter((error) => !ignoreErrors.includes(error))
      : filteredErrors;
    if (
      !errors.length &&
      ((account && amountType) ||
        isChannelConfiguration ||
        isTradingViewSignalsBot) &&
      (amountValue || botType === BotType.SIGNAL || isTradingViewSignalsBot) &&
      (entryStrategy ||
        botType === BotType.TRADING_VIEW ||
        isTradingViewSignalsBot ||
        isHedgingBot)
    ) {
      return getBaseParsedFormData(
        formValues,
        botType,
        checkedFields,
        overrideFields,
        exchangeSymbols,
        isChannelConfiguration
      ) as CreateBotRequest;
    }
  };

  const {
    parsedEntries,
    parsedTakeProfits,
    parsedStop,
    parsedValidationEntries,
    parsedEntriesAndGridTakeProfits,
  } = useBotOrders({
    formValues,
    botAmountData,
    getMinAmountByPrice,
    checkedFields,
    botType,
    symbolPriceData,
    isMobileApp,
    useSymbolFromAlert,
  });

  const initialBotAmountData = useMemo(
    () =>
      getBotInitialAmount(
        parsedValidationEntries,
        mainCoinAmountPrecision,
        botType
      ),
    [
      parsedValidationEntries,
      reverse,
      symbolPriceData,
      mainCoinAmountPrecision,
      botType,
    ]
  );

  const amountErrors = useMemo(
    () =>
      isBulkAction
        ? []
        : validateBotAmount(
            parsedValidationEntries,
            botAmountData,
            getBotAmountDataQuery.isFetching,
            isEdit,
            parsedEntryStrategy,
            initialBotAmountData,
            symbolPriceData?.data,
            botType,
            useSymbolFromAlert
          ),
    [
      parsedValidationEntries,
      getMinAmountByPrice,
      initialBotAmountData,
      botAmountData,
      useSymbolFromAlert,
    ]
  );
  useEffect(() => {
    AMOUNT_ERRORS.forEach((error) => {
      amountErrors.includes(error) ? addError(error) : removeError(error);
    });
  }, [amountErrors]);

  const isFirstEntryValid = validateCustomFirstEntry(parsedEntryStrategy);

  useEffect(() => {
    isFirstEntryValid
      ? removeError(Error.ENTRIES_RATIO_SUM)
      : addError(Error.ENTRIES_RATIO_SUM);
  }, [isFirstEntryValid]);

  const getBotGeneralData = () => {
    let botGeneralData:
      | BotGeneralData
      | BotData
      | BaseBotData
      | BaseBotGeneralData
      | undefined;
    if (isBacktesting) {
      botGeneralData = backtestBotData;
    } else if (isChannelConfiguration) {
      botGeneralData = botConfigurationData;
    } else if (isEdit || isDuplicate) {
      botGeneralData = editBotGeneralData?.data;
    } else if (isPublic) {
      botGeneralData = publicBotGeneralData;
    } else if (isBulkCreate && account) {
      botGeneralData = bulkCreateBotGeneralData?.[account.client_id];
    } else {
      botGeneralData = createBotGeneralData;
    }
    if (
      !isChannelConfiguration &&
      (isEdit || isDuplicate || isPublic) &&
      useBotCreateGeneralData &&
      botGeneralData &&
      createBotGeneralData
    ) {
      botGeneralData = {
        ...botGeneralData,
        options: {
          ...pickBy(
            botGeneralData.options,
            (_, key) => !OPTIONS_KEYS_TO_OVERRIDE.has(key as keyof BotOptions)
          ),
          ...pickBy(createBotGeneralData.options, (_, key) =>
            OPTIONS_KEYS_TO_OVERRIDE.has(key as keyof BotOptions)
          ),
        } as BotOptions,
        configurations: {
          ...createBotGeneralData.configurations,
          ...botGeneralData.configurations,
        },
      };
    }
    return botGeneralData;
  };

  const botGeneralData = useMemo(getBotGeneralData, [
    isBacktesting,
    botFormType,
    account,
    createBotGeneralData,
    bulkCreateBotGeneralData,
    editBotGeneralData,
    publicBotGeneralData,
    backtestBotData,
    useBotCreateGeneralData,
    botConfigurationData,
    isChannelConfiguration,
  ]);
  const isMarginExchange = !!botGeneralData?.options?.["Long/Short"];

  const queriesStatuses = [
    getDefaultConfigurationDataQuery,
    getBotDataQuery,
    getPublicBotDataQuery,
    getBotCreateGeneralDataQuery,
    getBotBulkCreateGeneralDataQuery,
    getBotAmountDataQuery,
    getBotsNamesQuery,
    getClientsListQuery,
    getExchangeSymbolPriceQuery,
    getExchangeToSymbolPricesQuery,
    getGroupsDataQuery,
    getBotsDataQuery,
    getBacktestBotDataQuery,
  ].filter((query) => !!query.endpointName);

  const getLoadingCheckQueries = () => {
    let loadingQueryStatuses = [...queriesStatuses, getSymbolsMetadataStatus];
    if (isEdit || isDuplicate || isPublic) {
      loadingQueryStatuses = loadingQueryStatuses.filter(
        (queryStatus) => queryStatus.endpointName !== "getBotCreateGeneralData"
      );
    }
    if (isBacktesting && initialized) {
      loadingQueryStatuses = loadingQueryStatuses.filter(
        (queryStatus) =>
          queryStatus.endpointName !== "getBotData" &&
          queryStatus.originalArgs?.is_backtesting
      );
    }
    return loadingQueryStatuses;
  };

  const loadingQueryStatuses = getLoadingCheckQueries();

  const refetchErrorQueries = () => {
    loadingQueryStatuses.forEach((queryStatus) => {
      if (queryStatus.isError && !queryStatus.data) queryStatus.refetch();
    });
  };

  const isFetching = queriesStatuses.some(
    (queryStatus) => queryStatus.isFetching || queryStatus.isLoading
  );

  const isLoading = loadingQueryStatuses.some((queryStatus) => {
    return (
      queryStatus.endpointName !== "getSubGroupBotConfigurations" &&
      !(
        queryStatus.endpointName === "getPublicBotData" && isPublicBotApplied
      ) &&
      queryStatus.isLoading &&
      (queryStatus.endpointName !== "getExchangeSymbolPrice" ||
        queryStatus.isUninitialized)
    );
  });

  const isError = loadingQueryStatuses.some(
    (queryStatus) => queryStatus.isError
  );

  useEffect(() => {
    let clearLoadingTimeout: NodeJS.Timeout;
    if (isLoading && !localIsLoading) {
      setLocalIsLoading(true);
    } else if (!isLoading && localIsLoading) {
      clearLoadingTimeout = setTimeout(() => {
        setLocalIsLoading(false);
      }, loadingEndDelayMs);
    }
    const clearInitializedDataTimeout = setTimeout(() => {
      addInitializedData(FormDataType.ACCOUNT_AND_SYMBOL);
    }, 4000);
    return () => {
      if (clearLoadingTimeout) {
        clearTimeout(clearLoadingTimeout);
      }
      if (clearInitializedDataTimeout) {
        clearTimeout(clearInitializedDataTimeout);
      }
    };
  }, [isLoading, localIsLoading]);

  const finalIsLoading = localIsLoading || isLoading;
  const dataInitialized =
    isBulkEdit || initializedData.has(FormDataType.ACCOUNT_AND_SYMBOL);
  const hideContent = finalIsLoading || !dataInitialized;
  const finalErrors = useMemo(() => {
    return finalIsLoading ? [] : filteredErrors;
  }, [finalIsLoading, filteredErrors]);

  const getIdToValue = () => {
    if (isBacktesting) return getBacktestBotDataQuery?.data?.id_to_value;
    return (
      (bulkCreateBotGeneralData?.id_to_value ??
        createBotGeneralData?.id_to_value ??
        editBotGeneralData?.id_to_value) ||
      {}
    );
  };

  return {
    formData: {
      formValues,
      formSetters,
      ...formValues,
      ...formSetters,
      addError,
      removeError,
      errors: finalErrors,
      checkedFields,
      toggleField,
      toggleOverrideField,
      botAmountData,
      getBotAmountDataStatus: getBotAmountDataQuery,
      getMinAmountByPrice,
      parsedEntries,
      initialized: initialized && !!symbolsMetadata,
      symbolPriceData,
      isSymbolValid,
      isMarginExchange,
      botId: editBotGeneralData?.data.id || publicBotGeneralData?.id,
      backtestingBotId,
      isFetching,
      isError,
      isLoading: hideContent,
      editBotGeneralData: editBotGeneralData?.data,
      publicBotGeneralData,
      validateOrderConvertedAmount,
      getBotCreateGeneralDataStatus: getBotCreateGeneralDataQuery,
      getBulkBotCreateGeneralDataStatus: getBotBulkCreateGeneralDataQuery,
      getBacktestBotDataStatus: getBacktestBotDataQuery,
      getExchangeToSymbolPricesStatus: getExchangeToSymbolPricesQuery,
      getExchangeSymbolPriceStatus: getExchangeSymbolPriceQuery,
      backtestBotData,
      getBotsDataStatus: getBotsDataQuery,
      addInitializedData,
      idToValue: getIdToValue(),
      refetchErrorQueries,
      getGroupsDataStatus: getGroupsDataQuery,
      fieldsToInitialize,
      addFieldToInitialize,
      removeFieldToInitialize,
      isAdvancedView,
      setIsAdvancedView,
      parsedValidationEntries,
      parsedTakeProfits,
      parsedStop,
      parsedEntriesAndGridTakeProfits,
      initialBotAmountData,
      getAmountTypePrecision,
      amountPrecision,
      tab,
      setTab,
      initializeFormValues,
      amountChangedRef,
      initializedData,
      showBacktesting,
      symbolsMetadata,
      showPublicBots,
      isPublicBotApplied,
      shouldInitialSymbolAndAccount,
      setShouldInitialSymbolAndAccount,
    },
    botGeneralData,
    getParsedFormData,
  };
};

export default useBotFormProvider;

const ERROR_TYPES_TO_TOGGLE_FIELDS: { [key in Error]?: ToggleField[] } = {
  [Error.DEFAULT_STOP]: [ToggleField.STOP],
  [Error.DEFAULT_TAKE_PROFIT]: [ToggleField.TAKE_PROFITS],
  [Error.TAKE_PROFIT_STRATEGY]: [
    ToggleField.TAKE_PROFITS,
    ToggleField.TAKE_PROFIT_STRATEGY,
  ],
  [Error.TAKE_PROFIT_PRICES]: [
    ToggleField.TAKE_PROFITS,
    ToggleField.TAKE_PROFIT_STRATEGY,
  ],
  [Error.ENTRY_STRATEGY]: [ToggleField.ENTRIES],
  [Error.ENTRY_PRICES]: [ToggleField.ENTRIES],
  [Error.ENTRIES_RATIO_SUM]: [ToggleField.ENTRIES],
  [Error.GRIDS_DATA]: [ToggleField.ENTRIES],
  [Error.CONSECUTIVE_GRIDS_PRICE_DISTANCE]: [ToggleField.ENTRIES],
};

const AMOUNT_ERRORS = [
  Error.INSUFFICIENT_AMOUNT,
  Error.MIN_ENTRY_ORDER_AMOUNT,
  Error.ACTIVE_ENTRIES_AMOUNT,
  Error.GENERAL_AMOUNT,
];

const OPTIONS_KEYS_TO_OVERRIDE: Set<keyof BotOptions> = new Set([
  "Stop Type",
  "Multiplier",
  "Long/Short",
  "Margin Type",
]);
